/**
 * چیدمان اصلی: دروازه‌ی رمز، ریل ناوبری، نوار بالا و ناحیه‌ی محتوا.
 *
 * دروازه پیش از هر چیز دیگری است. در حالت مشتری، تا رمز وارد نشود نه ریل دیده
 * می‌شود و نه هیچ درخواستی به مسیرهای مشاوره می‌رود.
 *
 * سه تصمیم چیدمانی:
 *   ۱. ریل تیره در دسکتاپ ثابت است و در موبایل به کشو تبدیل می‌شود؛ نسخه‌ی
 *      قبلی در موبایل هیچ راه ناوبری نداشت.
 *   ۲. نوار بالا شیشه‌ای و چسبان است تا حالت اجرا و خروج همیشه در دسترس باشند.
 *   ۳. عرض محتوا به حالت بستگی دارد: کارگاه پهن (جدول و فرم)، مشتری باریک
 *      (متن خوانا).
 */

import { LogOut, Menu, Moon, Sun, X } from 'lucide-react'
import { Dialog } from 'radix-ui'
import { useState } from 'react'
import { Outlet } from 'react-router'

import { Button } from '@/components/ui/Button'
import { Badge, Banner } from '@/components/ui/primitives'
import { AccessGate } from '@/features/auth/AccessGate'
import { AdminGate } from '@/features/auth/AdminGate'
import { useMeta } from '@/lib/meta-context'
import { ScopeProvider } from '@/lib/scope-context'
import { useThemeMode } from '@/lib/theme-mode'

import { Sidebar, SidebarNav } from './Sidebar'

export const AppShell = () => {
  const { meta, isAuthenticated, signOut } = useMeta()
  const { mode, toggle } = useThemeMode()
  const [drawerOpen, setDrawerOpen] = useState(false)

  if (!isAuthenticated) {
    // دو دروازه‌ی جدا: پنل مدیریت و نسخه‌ی مشتری. حالت اجرا از سرور می‌آید، نه
    // از مسیر یا متغیر build؛ پس با دستکاری URL نمی‌شود دروازه را عوض کرد.
    return meta.is_studio ? <AdminGate /> : <AccessGate />
  }

  return (
    <ScopeProvider>
      <div className="flex min-h-dvh">
        <Sidebar />

        {/*
          `min-w-0` لازم است: بدون آن، محتوای پهن (جدول یا متن بدون فاصله)
          ریل را از جا می‌کند، چون آیتم flex به‌طور پیش‌فرض کوچک‌تر از محتوایش
          نمی‌شود.
        */}
        <div className="flex min-w-0 flex-1 flex-col">
          <header className="glass sticky top-0 z-30 border-b border-line">
            <div className="flex h-14 items-center gap-3 px-4 sm:px-6">
              {/* کشوی ناوبری موبایل. در دسکتاپ ریل ثابت جایش را می‌گیرد. */}
              <Dialog.Root open={drawerOpen} onOpenChange={setDrawerOpen}>
                <Dialog.Trigger asChild>
                  <Button variant="ghost" size="icon" className="lg:hidden" aria-label="منو">
                    <Menu className="size-5" />
                  </Button>
                </Dialog.Trigger>
                <Dialog.Portal>
                  <Dialog.Overlay className="animate-fade fixed inset-0 z-40 bg-surface-inverse/45 backdrop-blur-sm" />
                  <Dialog.Content
                    className="fixed inset-y-0 start-0 z-50 w-72 bg-[linear-gradient(180deg,var(--t-rail-from),var(--t-rail-to))] shadow-overlay outline-none"
                    aria-describedby={undefined}
                  >
                    <Dialog.Title className="sr-only">ناوبری</Dialog.Title>
                    <Dialog.Close
                      className="absolute top-4 end-4 rounded-md p-1.5 text-rail-ink-faint transition-colors hover:bg-white/10 hover:text-white"
                      aria-label="بستن"
                    >
                      <X className="size-4" />
                    </Dialog.Close>
                    <SidebarNav
                      onNavigate={() => {
                        setDrawerOpen(false)
                      }}
                    />
                  </Dialog.Content>
                </Dialog.Portal>
              </Dialog.Root>

              <p className="truncate text-sm font-semibold text-ink lg:hidden">
                {meta.profile.app_title}
              </p>

              <div className="flex-1" />

              {/* حالت اجرا همیشه دیده می‌شود؛ کاربر باید بداند در کارگاه است یا
                  در نسخه‌ی مشتری. */}
              <Badge tone={meta.is_studio ? 'accent' : 'support'} dot className="hidden sm:flex">
                {meta.is_studio ? 'کارگاه' : 'نسخه‌ی مشتری'}
              </Badge>

              <Button
                variant="ghost"
                size="icon"
                onClick={toggle}
                aria-label={mode === 'dark' ? 'حالت روشن' : 'حالت تاریک'}
                title={mode === 'dark' ? 'حالت روشن' : 'حالت تاریک'}
              >
                {mode === 'dark' ? <Sun className="size-4.5" /> : <Moon className="size-4.5" />}
              </Button>

              {/* خروج تنها در نسخه‌ی مشتری معنا دارد؛ استودیو احراز هویت ندارد. */}
              {meta.access_required && (
                <Button variant="ghost" size="sm" icon={<LogOut className="size-4" />} onClick={signOut}>
                  <span className="hidden sm:inline">خروج</span>
                </Button>
              )}
            </div>
          </header>

          <main className="min-w-0 flex-1">
            <div
              className={
                meta.is_studio
                  ? 'mx-auto max-w-7xl px-4 py-7 sm:px-6 lg:px-8'
                  : 'mx-auto max-w-4xl px-4 py-7 sm:px-6'
              }
            >
              {/*
                پنل بی‌قفل یک شکاف است، نه یک راحتی. اگر `ADMIN_ACCESS_CODE`
                تنظیم نشده باشد، هشدار در هر صفحه دیده می‌شود تا روی سرور
                فراموش نشود.
              */}
              {meta.is_studio && !meta.admin_code_is_set && (
                <Banner tone="warning" title="پنل مدیریت رمز ندارد" className="mb-5">
                  مقدار <code>ADMIN_ACCESS_CODE</code> تنظیم نشده است، پس هر کسی که به این
                  آدرس برسد پنل را باز می‌کند. برای استقرار روی دامنه، تنظیمش الزامی است.
                </Banner>
              )}
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </ScopeProvider>
  )
}
